import { ref } from 'vue'
import type { ConversationSummary } from '@threadline/shared'

/**
 * Which conversations this person asked not to be interrupted by.
 *
 * Notifications are decided at the application root, so a message is heard whatever screen
 * somebody is on — but the conversation list lives inside the chat screen. This is the small
 * piece of state that carries the answer from one to the other, in the same module-level
 * shape `useColorScheme` and `useAccountBook` already use.
 *
 * It holds handles rather than account ids because a realtime event names the counterpart by
 * handle, and the answer is needed at the moment the event arrives.
 */
/**
 * Keyed by account id rather than by handle, because a group has an id and no handle — and
 * because an id is the identity while a handle is only an address it answers at.
 */
const quietCounterpartAccountIds = ref<Set<string>>(new Set())

/** Replaces what is known, from a freshly loaded conversation list. */
export function rememberQuietConversations(conversations: readonly ConversationSummary[]): void {
  quietCounterpartAccountIds.value = new Set(
    conversations
      .filter((conversation) => conversation.isMuted || conversation.isArchived)
      .map((conversation) => conversation.counterpartAccountId),
  )
}

export function useQuietConversations() {
  return {
    /**
     * Unknown means audible. A conversation nobody has loaded yet has not been muted by
     * anybody, and erring towards announcing is the safer of the two mistakes.
     */
    conversationStaysQuiet: (counterpartAccountId: string): boolean =>
      quietCounterpartAccountIds.value.has(counterpartAccountId),
    rememberQuietConversations,
  }
}
